/** Request bodies and response shapes for `/api/tickets`. Times are epoch milliseconds. */
import { z } from 'zod'
import { roleSchema } from '../roles.ts'
import { attachmentSchema } from './attachments.ts'
import { ACTIVITY_TYPES } from './engine.ts'

export const TITLE_MAX = 120
export const DESCRIPTION_MAX = 10_000
export const COMMENT_MAX = 10_000

/** Field errors, worded as the raise form shows them (DESIGN.md §3.6, PRD "Edge cases"). */
export const TICKET_FIELD_MESSAGES = {
  title: 'Add a short title so HR can spot this ticket.',
  titleTooLong: `Keep the title to ${TITLE_MAX} characters or fewer.`,
  description: 'Describe the issue so HR can act on it.',
  descriptionTooLong: `Keep the description under ${DESCRIPTION_MAX.toLocaleString('en-US')} characters.`,
  categoryId: 'Pick what the ticket is about.',
  priorityId: 'Pick how urgent it is.',
  raisedFor: 'Choose who this ticket is for.',
} as const

export type TicketCreateField = 'title' | 'description' | 'categoryId' | 'priorityId' | 'raisedFor'
export type TicketFieldErrors = Partial<Record<TicketCreateField, string>>

/** `POST /api/tickets` (shape only; the server checks content and returns field errors). */
export const ticketCreateSchema = z
  .object({
    title: z.string(),
    description: z.string(),
    categoryId: z.string(),
    priorityId: z.string(),
    confidential: z.boolean().default(false),
    /** Log on behalf of someone (HR only). */
    raisedFor: z.string().max(64).nullable().optional(),
  })
  .strict()
export type TicketCreateBody = z.input<typeof ticketCreateSchema>

/** Trims and checks a create body. Pure so the form and the server agree. */
export function checkTicketCreate(
  body: Pick<TicketCreateBody, 'title' | 'description' | 'categoryId' | 'priorityId'>,
): TicketFieldErrors {
  const errors: TicketFieldErrors = {}
  const title = body.title.trim()
  if (!title) errors.title = TICKET_FIELD_MESSAGES.title
  else if (title.length > TITLE_MAX) errors.title = TICKET_FIELD_MESSAGES.titleTooLong
  const description = body.description.trim()
  if (!description) errors.description = TICKET_FIELD_MESSAGES.description
  else if (description.length > DESCRIPTION_MAX)
    errors.description = TICKET_FIELD_MESSAGES.descriptionTooLong
  if (!body.categoryId) errors.categoryId = TICKET_FIELD_MESSAGES.categoryId
  if (!body.priorityId) errors.priorityId = TICKET_FIELD_MESSAGES.priorityId
  return errors
}

/** Optional optimistic-concurrency token on every ticket write. */
const versionField = z.number().int().nonnegative().optional()

export const commentCreateSchema = z
  .object({
    body: z.string().max(COMMENT_MAX),
    internal: z.boolean().default(false),
    version: versionField,
  })
  .strict()
export type CommentCreateBody = z.input<typeof commentCreateSchema>

export const reopenSchema = z.object({ version: versionField }).strict()

export const ticketSchema = z.object({
  id: z.number().int(),
  /** Display key, e.g. `HR-1001`. */
  key: z.string(),
  version: z.number().int(),
  title: z.string(),
  description: z.string(),
  categoryId: z.string(),
  priorityId: z.string(),
  requestedPriorityId: z.string(),
  statusId: z.string(),
  prevStatusId: z.string().nullable(),
  raisedBy: z.string(),
  loggedBy: z.string(),
  assignee: z.string().nullable(),
  confidential: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
  dueAt: z.number().nullable(),
  etaManual: z.boolean(),
  resolvedAt: z.number().nullable(),
  waitingSince: z.number().nullable(),
})
export type TicketDto = z.infer<typeof ticketSchema>

export const commentSchema = z.object({
  id: z.number().int(),
  ticketId: z.number().int(),
  authorId: z.string(),
  body: z.string(),
  internal: z.boolean(),
  createdAt: z.number(),
})
export type CommentDto = z.infer<typeof commentSchema>

export const activitySchema = z.object({
  id: z.number().int(),
  ticketId: z.number().int(),
  actorId: z.string(),
  type: z.enum(ACTIVITY_TYPES),
  fromValue: z.string().nullable(),
  toValue: z.string().nullable(),
  meta: z
    .object({
      auto: z.boolean().optional(),
      paused: z.number().optional(),
      reset: z.boolean().optional(),
    })
    .nullable(),
  createdAt: z.number(),
})
export type ActivityDto = z.infer<typeof activitySchema>

/** Just enough about a person to render a name, avatar and HR tag. */
export const personRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  dept: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: roleSchema,
  archived: z.boolean(),
})
export type PersonRef = z.infer<typeof personRefSchema>
export const peopleMapSchema = z.record(z.string(), personRefSchema)
export type PeopleMap = z.infer<typeof peopleMapSchema>

export const ticketSummarySchema = ticketSchema.extend({
  /** Comments the caller can see (public only for non-HR). */
  commentCount: z.number().int(),
})
export type TicketSummary = z.infer<typeof ticketSummarySchema>

/** `GET /api/tickets/mine` */
export const myTicketsResponseSchema = z.object({
  tickets: z.array(ticketSummarySchema),
  people: peopleMapSchema,
})
export type MyTicketsResponse = z.infer<typeof myTicketsResponseSchema>

/** `GET /api/tickets/:key` */
export const ticketDetailSchema = z.object({
  ticket: ticketSchema,
  /** Oldest first; internal notes only for HR. */
  comments: z.array(commentSchema),
  /** Newest first. */
  activity: z.array(activitySchema),
  /** Files on the ticket, oldest first. */
  attachments: z.array(attachmentSchema),
  people: peopleMapSchema,
})
export type TicketDetail = z.infer<typeof ticketDetailSchema>

/** `POST /api/tickets` → 201 */
export const ticketCreatedSchema = z.object({ ticket: ticketSchema })

/** `POST /api/tickets/:key/comments` → 201 */
export const commentCreatedSchema = z.object({
  comment: commentSchema,
  ticket: ticketSchema,
  /** The employee's reply moved a Waiting ticket back to HR. */
  resumed: z.boolean(),
})
export type CommentCreated = z.infer<typeof commentCreatedSchema>

/** `POST /api/tickets/:key/attachments` → 201 */
export const attachmentsUploadedSchema = z.object({
  attachments: z.array(attachmentSchema),
  ticket: ticketSchema,
})
export type AttachmentsUploaded = z.infer<typeof attachmentsUploadedSchema>

/** `POST /api/tickets/:key/reopen` → 200 */
export const ticketUpdatedSchema = z.object({ ticket: ticketSchema })

export const TICKET_CONFLICT_MESSAGE =
  'Someone else changed this ticket. Reload to see the latest version.'
